import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Spinner,
  Table,
} from 'reactstrap'
import React, { useState, useEffect } from 'react'
import {
  CashAdvanceStatus,
  cashAdvanceStatusToTitle,
  MerchantCashAdvanceDTO,
  RepaymentActivityType,
  PayoutMechanism,
} from './dtos/merchant-cash-advance.dto'
import { priceFormat } from '../../../helpers/utils'
import { useCashAdvanceRepaymentActivity } from './hooks/get-merchant-cash-advance-repayments.hook'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { useUpdateCashAdvancePayoutMechanism } from './hooks/update-cash-advance-payout-mechanism.hook'
import { useGetMerchantCashAdvances } from './hooks/get-mechant-cash-advances.hook'
import { useTriggerRemittance } from './hooks/trigger-remittance.hook'
import SupportUserApiDto from '../../../api-dtos/support/user/support-user-api.dto.interface'
import { getLoggedInSupportUser } from '../../../helpers/lopay_api_helper'
import TriggerRemittanceButton from './components/TriggerRemittanceButton'

const getPayoutMechanismBadge = (mechanism: PayoutMechanism) => {
  switch (mechanism) {
    case PayoutMechanism.Stripe:
      return <Badge className="bg-info me-2">Stripe</Badge>
    case PayoutMechanism.TrueLayer:
      return <Badge className="bg-warning me-2">True Layer</Badge>
    default:
      return null
  }
}

const getBadge = (status: CashAdvanceStatus) => {
  switch (status) {
    case CashAdvanceStatus.OffTrack:
      return <Badge className="bg-danger me-2">Off Track</Badge>
    case CashAdvanceStatus.OnTrack:
      return <Badge className="bg-success me-2">On Track</Badge>
    case CashAdvanceStatus.Repaid:
      return <Badge className="bg-primary me-2">Repaid</Badge>
    default:
      return null
  }
}

type RepaymentsTableProps = {
  isActive: boolean
  merchantId: string
}

const RepaymentsTable = (props: RepaymentsTableProps) => {
  const { isActive, merchantId } = props

  const results = useCashAdvanceRepaymentActivity({
    isActive,
    merchantId,
    pageSize: 20,
  })

  const data = React.useMemo(
    () => (results?.data?.pages ?? []).flatMap((page) => page.items),
    [results.data],
  )

  if (results.isLoading && data.length === 0) {
    return <Spinner className="ms-2" color="primary" />
  }

  if (results.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Repayment activity</CardTitle>
        </CardHeader>
        <CardBody>
          <div>
            Something went wrong whilst loading repayment activity. Please try
            again.
          </div>
        </CardBody>
      </Card>
    )
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Repayment activity</CardTitle>
        </CardHeader>
        <CardBody>
          <div
            className="table-responsive"
            style={{
              paddingBottom: 550,
              marginBottom: -550,
              msOverflowStyle: 'none',
            }}
          >
            The merchant has no repayment activity.
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Repayment history</CardTitle>
      </CardHeader>
      <CardBody>
        <div className="table-responsive">
          <Table className="table table-striped mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Sent to YouLend at</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => {
                const typeMap: Record<RepaymentActivityType, string> = {
                  cashAdvanceRepayment: 'repayment',
                  cashAdvanceRepaymentRefund: 'refund',
                }
                return (
                  <tr key={d.id}>
                    <td>
                      {moment(d.createdAt).local().format('DD-MM-YY hh:mm')}
                    </td>
                    <td>{typeMap[d.type]}</td>
                    <td>{priceFormat(d.amount)}</td>
                    <td>
                      {d.paidAt
                        ? moment(d.paidAt).local().format('DD-MM-YY hh:mm')
                        : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
          <br />
          {results.hasNextPage && (
            <Button
              type="button"
              color="primary"
              disabled={results.isLoading}
              onClick={() => results.fetchNextPage()}
            >
              Load more
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  )
}

type SwitchPayoutMechanismModalProps = {
  activeCashAdvance: MerchantCashAdvanceDTO
  showSwitchPayoutMechanismModal: boolean
  toggleShowSwitchPayoutMechanismModal: () => void
  loading: boolean
  onSubmit: (newPayoutMechanism: PayoutMechanism) => void
  error: boolean
}

const SwitchPayoutMechanismModal = ({
  activeCashAdvance,
  showSwitchPayoutMechanismModal,
  toggleShowSwitchPayoutMechanismModal,
  loading,
  onSubmit,
  error,
}: SwitchPayoutMechanismModalProps) => {
  const [newPayoutMechanism, setNewPayoutMechanism] = useState<PayoutMechanism>(
    activeCashAdvance.payoutMechanism,
  )

  return (
    <Modal
      isOpen={showSwitchPayoutMechanismModal}
      toggle={toggleShowSwitchPayoutMechanismModal}
    >
      <ModalHeader toggle={toggleShowSwitchPayoutMechanismModal}>
        Switch payout mechanism
      </ModalHeader>
      <ModalBody>
        <>
          <small>
            <b>Lopay ID:</b> {activeCashAdvance.id || '-'}
          </small>
          <br />
          <small>
            <b>YouLend Lead ID:</b> {activeCashAdvance.leadId || '-'}
          </small>
          <br />
          <small>
            <b>Current payout mechanism:</b>{' '}
            {getPayoutMechanismBadge(activeCashAdvance.payoutMechanism)}
          </small>
          <br />
          <br />
          <FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  name="payoutMechanism"
                  type="radio"
                  checked={newPayoutMechanism === PayoutMechanism.Stripe}
                  onChange={() => setNewPayoutMechanism(PayoutMechanism.Stripe)}
                  disabled={loading}
                />
                Stripe
              </Label>
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  name="payoutMechanism"
                  type="radio"
                  checked={newPayoutMechanism === PayoutMechanism.TrueLayer}
                  onChange={() =>
                    setNewPayoutMechanism(PayoutMechanism.TrueLayer)
                  }
                  disabled={loading}
                />
                True Layer
              </Label>
            </FormGroup>
          </FormGroup>
        </>
        {error && (
          <div className="mt-3">
            <b>Something went wrong...</b>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          disabled={loading}
          onClick={toggleShowSwitchPayoutMechanismModal}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={loading}
          onClick={() => {
            if (
              activeCashAdvance.payoutMechanism === newPayoutMechanism ||
              !newPayoutMechanism
            )
              return
            onSubmit(newPayoutMechanism)
          }}
        >
          {loading ? 'Switching...' : 'Switch mechanism'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

type CashAdvanceProps = {
  cashAdvance: MerchantCashAdvanceDTO
  setActiveCashAdvance: () => void
}

const CashAdvance = (props: CashAdvanceProps) => {
  const { cashAdvance } = props

  const amount = cashAdvance.appliedAmount
    ? priceFormat(cashAdvance.appliedAmount)
    : '-'

  const repaid = cashAdvance.amountRepaid
    ? priceFormat(cashAdvance.amountRepaid)
    : '-'

  const owed =
    cashAdvance.amountRemaining && cashAdvance.amountRepaid
      ? priceFormat({
          units:
            cashAdvance.amountRemaining.units + cashAdvance.amountRepaid.units,
          currencyCode: cashAdvance.amountRemaining.currencyCode,
        })
      : '-'

  const percentage = cashAdvance.paidOffPercentage
    ? (cashAdvance.paidOffPercentage.value /
        cashAdvance.paidOffPercentage.precision) *
      100
    : 0

  const shouldShowProgress = [
    CashAdvanceStatus.OnTrack,
    CashAdvanceStatus.OffTrack,
    CashAdvanceStatus.Repaid,
  ].includes(cashAdvance.status)

  return (
    <Card className={cashAdvance.isDisplayed ? 'border-primary' : ''}>
      <CardBody>
        {shouldShowProgress ? (
          <>
            <div className="d-flex align-items-center">
              <div className="me-2">
                <b>Amount: {amount}</b> (Total to repay: {owed})
              </div>
              {cashAdvance.isDisplayed && (
                <Badge className="bg-primary me-2">Displayed in-app</Badge>
              )}
              {getBadge(cashAdvance.status)}
              {getPayoutMechanismBadge(cashAdvance.payoutMechanism)}
            </div>
            <small>
              {repaid} / {owed} repaid
            </small>
            <Progress
              value={percentage}
              color="primary"
              className="progress-xl mt-2"
            >
              {percentage}%
            </Progress>
          </>
        ) : (
          <div className="me-2">
            <b>Application status:</b>{' '}
            {cashAdvanceStatusToTitle[cashAdvance.status]}
          </div>
        )}
        <br />
        <small>
          <b>Lopay ID:</b> {cashAdvance.id || '-'}
        </small>
        <br />
        <small>
          <b>YouLend Lead ID:</b> {cashAdvance.leadId || '-'}
        </small>
        <br />
        {cashAdvance.youLendDashboardUrl && (
          <button
            type="button"
            onClick={async () => {
              if (cashAdvance.youLendDashboardUrl) {
                window.open(cashAdvance.youLendDashboardUrl, '_blank')
              }
            }}
            className="btn btn-light btn-sm waves-effect waves-light m-1"
          >
            <i className="fas fa-money-bill-alt font-size-16 align-middle me-2"></i>{' '}
            View in YouLend Dashboard
          </button>
        )}
        <Link
          onClick={(e) => {
            e.preventDefault()
            props.setActiveCashAdvance()
          }}
          to="#"
          className="btn btn-sm btn-primary"
        >
          Switch payout mechanism
        </Link>
      </CardBody>
    </Card>
  )
}

type MerchantCashAdvancesProps = {
  isActive: boolean
  merchantId: string
}

const MerchantCashAdvances = (props: MerchantCashAdvancesProps) => {
  const { isActive, merchantId } = props

  const [activeCashAdvanceId, setActiveCashAdvanceId] = useState<
    string | undefined
  >()
  const [showSwitchPayoutMechanismModal, setShowSwitchPayoutMechanismModal] =
    useState<boolean>(false)
  const [supportUser, setSupportUser] = useState<SupportUserApiDto | null>(null)

  useEffect(() => {
    const loggedInUser = getLoggedInSupportUser()
    setSupportUser((loggedInUser?.user as SupportUserApiDto) ?? null)
  }, [])

  const { data: queryData, isLoading: queryIsLoading } =
    useGetMerchantCashAdvances(merchantId)

  const {
    mutate: updatePayoutMechanism,
    isLoading: mutationIsLoading,
    isError,
    reset,
  } = useUpdateCashAdvancePayoutMechanism(merchantId)

  const {
    mutate: triggerRemittance,
    isLoading: triggerRemittanceLoading,
    isError: triggerRemittanceError,
    isSuccess: triggerRemittanceSuccess,
  } = useTriggerRemittance(merchantId)

  const toggleShowSwitchPayoutMechanismModal = () => {
    setShowSwitchPayoutMechanismModal(!showSwitchPayoutMechanismModal)
    reset()
  }

  const loading =
    queryIsLoading || mutationIsLoading || triggerRemittanceLoading

  const onSubmit = (newPayoutMechanism: PayoutMechanism) => {
    if (loading || !activeCashAdvanceId) return

    updatePayoutMechanism({
      cashAdvanceId: activeCashAdvanceId,
      newPayoutMechanism,
    })
  }

  const { cashAdvances } = queryData || {}
  const isQualified = cashAdvances?.length

  // Check if there are cash advances eligible for remittance (OnTrack or OffTrack)
  const hasEligibleCashAdvances = cashAdvances?.some(
    (ca) =>
      ca.status === CashAdvanceStatus.OnTrack ||
      ca.status === CashAdvanceStatus.OffTrack,
  )

  // Check if user is super user
  const isSuperUser = supportUser?.accessRights === 'SUPER_USER'

  // Show trigger remittance button only for super users with eligible cash advances
  const showTriggerRemittanceButton = isSuperUser && hasEligibleCashAdvances

  const activeCashAdvance = cashAdvances?.find(
    (ca) => ca.id === activeCashAdvanceId,
  )

  return (
    <>
      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <CardTitle className="mb-0">Cash advances</CardTitle>
            {showTriggerRemittanceButton && (
              <TriggerRemittanceButton
                isLoading={triggerRemittanceLoading}
                isDisabled={loading}
                onTrigger={() => triggerRemittance()}
              />
            )}
          </div>
          {showTriggerRemittanceButton && triggerRemittanceSuccess && (
            <div className="alert alert-success mt-2 mb-0" role="alert">
              <i className="fas fa-check-circle me-2"></i>
              Remittance processing initiated successfully!
            </div>
          )}
          {showTriggerRemittanceButton && triggerRemittanceError && (
            <div className="alert alert-danger mt-2 mb-0" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Failed to trigger remittance. Please try again.
            </div>
          )}
        </CardHeader>
        <CardBody>
          {cashAdvances ? (
            isQualified ? (
              cashAdvances.map((ca) => (
                <CashAdvance
                  key={ca.id}
                  cashAdvance={ca}
                  setActiveCashAdvance={() => {
                    setActiveCashAdvanceId(ca.id)
                    setShowSwitchPayoutMechanismModal(true)
                  }}
                />
              ))
            ) : (
              <div>Merchant does not qualify for cash advances.</div>
            )
          ) : (
            <div>Loading...</div>
          )}
        </CardBody>
      </Card>
      <RepaymentsTable isActive={isActive} merchantId={merchantId} />
      {activeCashAdvance && (
        <SwitchPayoutMechanismModal
          key={activeCashAdvance.id}
          activeCashAdvance={activeCashAdvance}
          showSwitchPayoutMechanismModal={showSwitchPayoutMechanismModal}
          toggleShowSwitchPayoutMechanismModal={
            toggleShowSwitchPayoutMechanismModal
          }
          loading={loading}
          onSubmit={onSubmit}
          error={isError}
        />
      )}
    </>
  )
}

export default MerchantCashAdvances
